import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PhoneIncoming, ScanSearch, Check } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/shared/page-header'
import { CustomersTabs } from './customers-tabs'
import { StatCard } from '@/components/shared/stat-card'
import { EmptyState } from '@/components/shared/feedback'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/misc'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { initials } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import { Link } from 'react-router-dom'
import { useExtractPhones, type ExtractPhonesResult, type PhoneMatch } from '@/hooks/use-contacts'

export function PhoneExtractPage() {
  const { t } = useTranslation('custList')
  const extract = useExtractPhones()
  const [matches, setMatches] = useState<PhoneMatch[] | null>(null)
  const [scanned, setScanned] = useState<number | null>(null)
  const [conflicts, setConflicts] = useState<NonNullable<ExtractPhonesResult['conflicts']>>([])
  const [excluded, setExcluded] = useState<Set<string>>(new Set())

  const onScan = () => {
    extract.mutate(
      { dryRun: true },
      {
        onSuccess: (r) => {
          setMatches(r.matches ?? [])
          setScanned(r.scanned)
          setConflicts(r.conflicts ?? [])
          setExcluded(new Set())
          toast.success(t('phoneExtract.scanned', { scanned: r.scanned, matched: r.matched }))
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  const toggle = (id: string) =>
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const selectedCount = (matches?.length ?? 0) - excluded.size

  const onApply = () => {
    if (!matches?.length) return
    if (selectedCount === 0) {
      toast.error(t('phoneExtract.noneSelected'))
      return
    }
    if (!confirm(t('phoneExtract.confirmApply', { count: selectedCount }))) return
    extract.mutate(
      { dryRun: false, excludeIds: [...excluded] },
      {
        onSuccess: (r) => {
          toast.success(t('phoneExtract.updated', { count: r.updated }))
          setMatches(null)
          setScanned(null)
          setExcluded(new Set())
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  const isApplying = extract.isPending

  return (
    <div className="space-y-6">
      <CustomersTabs />
      <PageHeader
        title={t('phoneExtract.title')}
        description={t('phoneExtract.description')}
        actions={
          <Button onClick={onScan} disabled={isApplying}>
            <ScanSearch className="h-4 w-4" /> {t('phoneExtract.scan')}
          </Button>
        }
      />

      {matches === null ? (
        <EmptyState
          icon={PhoneIncoming}
          title={t('phoneExtract.notScannedTitle')}
          description={t('phoneExtract.notScannedDescription')}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard label={t('phoneExtract.statScanned')} value={scanned ?? 0} icon={ScanSearch} hint={t('phoneExtract.statScannedHint')} />
            <StatCard label={t('phoneExtract.statFound')} value={matches.length} icon={PhoneIncoming} tone="success" />
            <StatCard label={t('phoneExtract.statWillUpdate')} value={selectedCount} icon={Check} tone="warning" />
          </div>

          {conflicts.length > 0 && (
            <Card>
              <CardHeader className="space-y-1">
                <CardTitle className="text-base">{t('phoneExtract.conflictsTitle', { count: conflicts.length })}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {t('phoneExtract.conflictsHint')}
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {conflicts.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-lg border p-3">
                    <p className="min-w-0 flex-1 truncate font-medium">{m.fullName || t('phoneExtract.noName')}</p>
                    <span className="shrink-0 tabular-nums text-muted-foreground">{m.extractedPhone}</span>
                    <Link to={`/customers/${m.existingContactId}`} className="shrink-0 text-sm text-primary hover:underline">
                      {t('phoneExtract.viewHolder')}
                    </Link>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {matches.length === 0 ? (
            <EmptyState title={t('phoneExtract.noneTitle')} description={t('phoneExtract.noneDescription')} />
          ) : (
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">{t('phoneExtract.resultsTitle', { count: matches.length })}</CardTitle>
                <Button onClick={onApply} disabled={isApplying || selectedCount === 0}>
                  <Check className="h-4 w-4" />
                  {isApplying ? t('phoneExtract.applying') : t('phoneExtract.apply', { count: selectedCount })}
                </Button>
              </CardHeader>
              <CardContent className="space-y-2">
                {matches.map((m) => {
                  const checked = !excluded.has(m.id)
                  return (
                    <label
                      key={m.id}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/40"
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggle(m.id)} />
                      <Avatar className="h-9 w-9">
                        <AvatarFallback>{initials(m.fullName)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{m.fullName || t('phoneExtract.noName')}</p>
                      </div>
                      <span className="shrink-0 font-semibold tabular-nums text-primary">
                        {m.extractedPhone}
                      </span>
                    </label>
                  )
                })}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
